import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, UserCheck, Building2, Radar, PencilLine, KeyRound, Ban, ShieldCheck, Trash2, Search, Flag, Tags, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { EmptyState } from '@/components/common/Primitives';
import { PageTitle, StatusBadge } from '@/pages/dashboard/DashboardPages';
import { useData } from '@/hooks/useData';
import { supabase } from '@/lib/supabase';
import { adminCall } from '@/services/admin';
import { reportService } from '@/services/api';
import { REPORT_REASON_NAME } from '@/data/taxonomy';
import { formatDate } from '@/lib/format';
import { useSeo } from '@/lib/seo';

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="surface p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="h-4 w-4 text-primary" />{label}</div>
    <div className="mt-2 font-display text-3xl font-semibold tabular-nums">{value ?? '—'}</div>
  </div>
);

export function AdminOverview() {
  useSeo({ title: 'Administração · Indicadores' });
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    adminCall('stats').then(setStats).catch((e) => setError(e.message));
  }, []);
  return (
    <div data-testid="admin-overview">
      <PageTitle title="Indicadores" text="Visão geral da rede." />
      {error && <p className="mb-4 text-sm text-[rgb(255,150,165)]">{error}</p>}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Users} label="Contas cadastradas" value={stats?.total} testId="admin-stat-total" />
        <Stat icon={UserCheck} label="Ativos nas últimas 24 horas" value={stats?.active24h} testId="admin-stat-active" />
        <Stat icon={Building2} label="Empresas" value={stats?.companies} testId="admin-stat-companies" />
        <Stat icon={Radar} label="Oportunidades ativas" value={stats?.opportunities} testId="admin-stat-opportunities" />
      </div>
    </div>
  );
}

export function AdminUsers() {
  useSeo({ title: 'Administração · Contas' });
  const { user: me } = useData();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'empresario' });
  const [pwUser, setPwUser] = useState(null);
  const [password, setPassword] = useState('');
  const [banTarget, setBanTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const res = await adminCall('list');
      setUsers(res.users);
    } catch (e) {
      toast.error(e.message);
      setUsers([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) => [u.name, u.email, u.company?.name].some((v) => (v || '').toLowerCase().includes(term)));
  }, [users, q]);

  const saveEdit = () => run(async () => {
    await adminCall('update', editing.id, editForm);
    setEditing(null);
  }, 'Conta atualizada');

  const savePassword = () => run(async () => {
    await adminCall('password', pwUser.id, { password });
    setPwUser(null);
    setPassword('');
  }, 'Senha alterada');

  const confirmBan = () => {
    const action = banTarget.banned ? 'unban' : 'ban';
    const msg = banTarget.banned ? 'Conta reativada' : 'Conta banida';
    return run(async () => {
      await adminCall(action, banTarget.id);
      setBanTarget(null);
    }, msg);
  };

  const confirmDelete = () => run(async () => {
    await adminCall('delete', deleteTarget.id);
    setDeleteTarget(null);
  }, 'Conta excluída');

  return (
    <div data-testid="admin-users">
      <PageTitle title="Contas" text="Todos os usuários cadastrados. Cada ação é validada no servidor." />
      <div className="mb-4 flex items-center gap-2 rounded-md border border-white/10 bg-[#0b0f16] px-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, e-mail ou empresa" className="border-0 bg-transparent focus-visible:ring-0" data-testid="admin-users-search" />
      </div>

      {users === null ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : filtered.length ? (
        <div className="surface divide-y divide-white/5 overflow-hidden">
          {filtered.map((u) => {
            const isMe = u.id === me?.id;
            return (
              <div key={u.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center" data-testid="admin-user-row">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{u.name || 'Sem nome'}</span>
                    {u.role === 'admin' && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-cyan-300/25 bg-cyan-300/10 px-1.5 py-0.5 text-[11px] text-cyan-100"><ShieldCheck className="h-3 w-3" /> Administrador</span>
                    )}
                    {u.banned ? <StatusBadge status="suspensa" /> : <StatusBadge status="ativa" />}
                    {isMe && <span className="text-[11px] text-muted-foreground">(você)</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{u.email}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {u.company ? (
                      <>Empresa: <Link to={`/empresas/${u.company.id}`} className="text-foreground hover:text-primary">{u.company.name}</Link></>
                    ) : 'Sem empresa cadastrada'}
                    {' · '}Cadastro {formatDate(u.createdAt)}
                    {' · '}Último acesso {u.lastSignIn ? formatDate(u.lastSignIn) : 'nunca'}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="secondary" className="border border-white/10" onClick={() => { setEditing(u); setEditForm({ name: u.name, role: u.role }); }} disabled={busy} data-testid="admin-user-edit">
                    <PencilLine /> Editar
                  </Button>
                  <Button size="sm" variant="secondary" className="border border-white/10" onClick={() => { setPwUser(u); setPassword(''); }} disabled={busy} data-testid="admin-user-password">
                    <KeyRound /> Senha
                  </Button>
                  {!isMe && (
                    <Button size="sm" variant="secondary" className="border border-white/10" onClick={() => setBanTarget(u)} disabled={busy} data-testid="admin-user-ban">
                      <Ban /> {u.banned ? 'Reativar' : 'Banir'}
                    </Button>
                  )}
                  {!isMe && (
                    <Button size="sm" variant="ghost" className="text-[rgb(255,130,150)] hover:bg-[rgba(255,59,92,0.08)]" onClick={() => setDeleteTarget(u)} disabled={busy} data-testid="admin-user-delete">
                      <Trash2 /> Excluir
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState icon={Users} title="Nenhuma conta encontrada" text="Tente outro termo de busca." testId="admin-users-empty" />
      )}

      {/* Editar */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="border-white/10 bg-[#0b0f16]">
          <DialogHeader>
            <DialogTitle>Editar conta</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="adm-name">Nome</Label>
              <Input id="adm-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="adm-role">Papel</Label>
              <select
                id="adm-role"
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                disabled={editing?.id === me?.id}
                className="h-9 rounded-md border border-white/10 bg-[#0b0f16] px-3 text-sm"
              >
                <option value="empresario">Empresário</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button className="btn-glow" onClick={saveEdit} disabled={busy}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trocar senha */}
      <Dialog open={!!pwUser} onOpenChange={(v) => !v && setPwUser(null)}>
        <DialogContent className="border-white/10 bg-[#0b0f16]">
          <DialogHeader>
            <DialogTitle>Trocar senha</DialogTitle>
            <DialogDescription>{pwUser?.email} · mínimo de 8 caracteres.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="adm-pw">Nova senha</Label>
            <Input id="adm-pw" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwUser(null)}>Cancelar</Button>
            <Button className="btn-glow" onClick={savePassword} disabled={busy || password.length < 8}>Salvar senha</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Banir / reativar */}
      <AlertDialog open={!!banTarget} onOpenChange={(v) => !v && setBanTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0b0f16]">
          <AlertDialogHeader>
            <AlertDialogTitle>{banTarget?.banned ? 'Reativar conta?' : 'Banir conta?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {banTarget?.banned
                ? `${banTarget?.email} voltará a entrar e a empresa e as publicações voltam a aparecer.`
                : `${banTarget?.email} não conseguirá entrar. A empresa e as publicações saem do ar, mas nada é apagado e pode ser revertido.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBan} data-testid="admin-ban-confirm">{banTarget?.banned ? 'Reativar' : 'Banir'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Excluir */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0b0f16]">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conta?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.email} será apagada, junto com a empresa e as publicações. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={confirmDelete} data-testid="admin-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function AdminCompanies() {
  useSeo({ title: 'Administração · Empresas' });
  const [rows, setRows] = useState(null);
  useEffect(() => {
    supabase
      .from('companies')
      .select('id, name, status, uf, city, created_at')
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) toast.error(error.message);
        setRows(data || []);
      });
  }, []);
  return (
    <div data-testid="admin-companies">
      <PageTitle title="Empresas" text="Empresas cadastradas. Para banir ou excluir, use a tela de Contas." />
      {rows === null ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : rows.length ? (
        <div className="surface divide-y divide-white/5 overflow-hidden">
          {rows.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-4">
              <Link to={`/empresas/${c.id}`} className="min-w-0 flex-1 truncate font-medium hover:text-primary">{c.name}</Link>
              <span className="hidden text-xs text-muted-foreground sm:inline">{c.city} · {c.uf}</span>
              <StatusBadge status={c.status} />
            </div>
          ))}
        </div>
      ) : <EmptyState icon={Building2} title="Nenhuma empresa cadastrada" testId="admin-companies-empty" />}
    </div>
  );
}

export function AdminOpportunities() {
  useSeo({ title: 'Administração · Oportunidades' });
  const [rows, setRows] = useState(null);
  useEffect(() => {
    supabase
      .from('opportunities')
      .select('id, title, status, uf, city, published_at')
      .order('published_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) toast.error(error.message);
        setRows(data || []);
      });
  }, []);
  return (
    <div data-testid="admin-opportunities">
      <PageTitle title="Oportunidades" text="Todas as publicações. Para remover, o dono ou o administrador usa a própria tela de publicações." />
      {rows === null ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : rows.length ? (
        <div className="surface divide-y divide-white/5 overflow-hidden">
          {rows.map((o) => (
            <div key={o.id} className="flex items-center gap-3 p-4">
              <Link to={`/oportunidades/${o.id}`} className="min-w-0 flex-1 truncate font-medium hover:text-primary">{o.title}</Link>
              <span className="hidden text-xs text-muted-foreground sm:inline">{o.city} · {o.uf}</span>
              <StatusBadge status={o.status} />
            </div>
          ))}
        </div>
      ) : <EmptyState icon={Radar} title="Nenhuma publicação" testId="admin-opportunities-empty" />}
    </div>
  );
}

export function AdminReports() {
  useSeo({ title: 'Administração · Denúncias' });
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('aberta');

  const load = async () => {
    try {
      setRows(await reportService.list());
    } catch (e) {
      toast.error(e.message);
      setRows([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const decide = async (r, status) => {
    setBusy(true);
    try {
      await reportService.resolve(r.id, status);
      toast.success(status === 'procedente' ? 'Denúncia marcada como procedente' : 'Denúncia marcada como improcedente');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const list = (rows || []).filter((r) => r.status === filter);
  const TABS = [['aberta', 'Abertas'], ['procedente', 'Procedentes'], ['improcedente', 'Improcedentes']];

  return (
    <div data-testid="admin-reports">
      <PageTitle title="Denúncias" text="Fila de moderação. Analise cada caso e decida." />
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map(([v, l]) => (
          <Button key={v} size="sm" variant={filter === v ? 'default' : 'secondary'} className={filter === v ? 'btn-glow' : 'border border-white/10'} onClick={() => setFilter(v)} data-testid={`admin-reports-tab-${v}`}>
            {l} <span className="ml-1.5 font-mono text-[11px] opacity-70">{(rows || []).filter((r) => r.status === v).length}</span>
          </Button>
        ))}
      </div>
      {rows === null ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : list.length ? (
        <div className="surface divide-y divide-white/5 overflow-hidden">
          {list.map((r) => (
            <div key={r.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-start" data-testid="admin-report-row">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[11px]">{r.target_type === 'empresa' ? 'Empresa' : 'Oportunidade'}</span>
                  <span className="text-sm font-medium">{REPORT_REASON_NAME[r.reason] || r.reason}</span>
                  <StatusBadge status={r.status} />
                </div>
                {r.details && <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{r.details}</p>}
                <div className="mt-2 font-mono text-[11px] text-muted-foreground">Denunciado em {formatDate(r.created_at)}</div>
                <div className="mt-2 text-xs">
                  <Link to={r.target_type === 'empresa' ? `/empresas/${r.target_id}` : `/oportunidades/${r.target_id}`} className="text-primary hover:underline">Ver publicação</Link>
                </div>
              </div>
              {r.status === 'aberta' && (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" className="border border-white/10" disabled={busy} onClick={() => decide(r, 'improcedente')} data-testid="admin-report-dismiss">Improcedente</Button>
                  <Button size="sm" className="btn-glow" disabled={busy} onClick={() => decide(r, 'procedente')} data-testid="admin-report-uphold">Procedente</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={Flag} title="Nada por aqui" text="Nenhuma denúncia nesta categoria." testId="admin-reports-empty" />
      )}
    </div>
  );
}

export function AdminCategories() {
  useSeo({ title: 'Administração · Categorias' });
  const [rows, setRows] = useState(null);
  useEffect(() => {
    supabase
      .from('categories')
      .select('id, name, active')
      .order('name')
      .then(({ data, error }) => {
        if (error) toast.error(error.message);
        setRows(data || []);
      });
  }, []);
  return (
    <div data-testid="admin-categories">
      <PageTitle title="Categorias" text="Lista de categorias da rede." />
      {rows === null ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : (
        <div className="surface divide-y divide-white/5 overflow-hidden">
          {rows.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-4">
              <Tags className="h-4 w-4 text-primary" />
              <span className="flex-1 text-sm">{c.name}</span>
              <span className="text-xs text-muted-foreground">{c.active ? 'Ativa' : 'Inativa'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminSponsored() {
  useSeo({ title: 'Administração · Anúncios patrocinados' });
  return (
    <div data-testid="admin-sponsored">
      <PageTitle title="Anúncios patrocinados" text="Itens patrocinados são sempre identificados." />
      <EmptyState icon={Megaphone} title="Em breve" text="Os anúncios patrocinados ainda não estão ativos. Nenhuma cobrança é feita." testId="admin-sponsored-pending" />
    </div>
  );
}